/**
 * Newsletter generation core — extracted from NewsletterAIService.js as
 * part of #229 Wave 4 (the final wave of the decomposition sequence).
 *
 * This module holds the single biggest method on NewsletterAIService —
 * `generateNewsletter` — which orchestrates:
 *   - auth + budget gate
 *   - advisor preferences + CommunicationStyle injection
 *   - placeholder pre-filling (Rule 17 — real S3 data, NOT static samples)
 *   - Phase 1B niche + series context (#223)
 *   - market context injection (#47)
 *   - provider routing + failover (#36)
 *   - usage stats tracking + result shaping
 *
 * The function takes the service instance as its first argument so it can
 * reuse the class's apiEndpoint / logger / auth / budget plumbing without
 * dragging that state into the module.
 *
 * Public exports:
 *   - generateNewsletter(service, params) — service-bound. The class method
 *     becomes a 1-line delegate.
 */

import lambdaPlaceholderService from '../LambdaPlaceholderService.js';
import editorialBriefService from '../EditorialBriefService.js';
import {
  createNetworkError,
  createDataUnavailableError,
} from '../../utils/StandardErrorFormat.js';
import { DEFAULT_MODEL, AI_PROVIDERS, getModelsByProvider } from '../../config/aiModels.js';

/**
 * #313 — Assemble the editorial-brief block for the initial-generation
 * system prompt.
 *
 * Returns a multi-line string listing every section's approved thesis, or
 * '' when no usable brief data is available. Pure-ish helper (service is
 * only used for logging); does its own JSON parsing for AppSync's
 * `a.json()` AWSJSON scalar which can arrive as either a string or a
 * parsed object depending on the client version.
 *
 * Failure modes (flag off, no series, lookup throws, empty sectionPlans,
 * every thesis empty) MUST return '' so the caller's prompt assembly
 * stays behaviour-neutral.
 */
async function assembleEditorialBriefBlock(service, { seriesId, campaignId }) {
  if (!editorialBriefService.isEnabled()) return '';
  if (!seriesId) return '';

  let brief;
  try {
    brief = await editorialBriefService.getActiveBriefForCampaign({
      seriesId,
      campaignId: campaignId || null,
    });
  } catch (err) {
    service.logger.warn('[EditorialBrief] lookup failed in generateNewsletter:', err);
    return '';
  }
  if (!brief) return '';

  // sectionPlans is `a.json()` in the schema — AppSync may return it as
  // either a JSON-encoded string or an already-parsed object.
  let plans = brief.sectionPlans;
  if (typeof plans === 'string') {
    try {
      plans = JSON.parse(plans);
    } catch {
      service.logger.warn('[EditorialBrief] sectionPlans was not valid JSON, ignoring');
      return '';
    }
  }
  if (!plans || typeof plans !== 'object') return '';

  const entries = Object.entries(plans)
    .map(([key, value]) => {
      const thesis = (value && typeof value === 'object' && typeof value.thesis === 'string')
        ? value.thesis.trim()
        : '';
      return thesis ? { key, thesis } : null;
    })
    .filter(Boolean);

  if (entries.length === 0) return '';

  const humanize = (k) => k.replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const lines = entries.map(({ key, thesis }) => `- ${humanize(key)}: ${thesis}`);

  console.log('[EditorialBrief] applied in generateNewsletter', {
    campaignId: campaignId || null,
    seriesId,
    briefId: brief.id,
    sectionCount: entries.length,
  });

  return [
    'EDITORIAL BRIEF (approved theses):',
    'The following theses guide each section. Honor them while preserving compliance.',
    '',
    ...lines,
  ].join('\n');
}

/**
 * Generate a newsletter end-to-end. Service-bound: uses the caller's
 * apiEndpoint + auth + logger + budget plumbing + marketContextService +
 * writingStyleService and class-internal helpers.
 *
 * @param {Object} service - NewsletterAIService instance
 * @param {Object} params - Generation parameters
 * @returns {Promise<Object>} { content, placeholderValues, metadata, success }
 */
export async function generateNewsletter(service, params) {
  await service._assertWithinDailyBudget();  // M3 #151 P0 #1
  service.logger.info('🤖 generateNewsletter called with:', {
    hasTemplateContent: !!params.templateContent,
    templateContentLength: params.templateContent?.length,
    templateContentPreview: params.templateContent?.substring(0, 200) + '...',
    hasTemplate: !!params.template,
    templateName: params.template?.name,
    templateId: params.template?.id,
    includeMarketContext: params.includeMarketContext,
    generatePlaceholders: params.generatePlaceholders,
    clientName: params.clientData?.name
  });

  // Ensure service is initialized
  await service.initialize();

  if (!service.apiEndpoint) {
    createDataUnavailableError('NewsletterAI Service not properly initialized. Deploy backend first.');
  }

  try {
    // Get authentication token
    const token = await service.getAuthToken();

    // Get advisor writing preferences (US-047 Phase 2-MVP)
    const advisorPrefs = await service._getAdvisorPreferences(params.userId);

    // Build style-specific system prompt
    const stylePrompt = service.writingStyleService.buildStylePrompt(advisorPrefs);

    // Sprint 4 #67 — Fetch the advisor's saved 9-dimension CommunicationStyle
    // and inject it into the system prompt so the LLM matches their voice.
    // Caller can opt out by passing useStyleProfile: false.
    const useStyleProfile = params.useStyleProfile !== false;
    let communicationStyle = null;
    let communicationStyleApplied = false;
    let communicationStyleBlock = '';

    if (useStyleProfile) {
      communicationStyle = await service._getAdvisorCommunicationStyle();
      // Build block from saved style OR sensible defaults (block builder
      // handles null by returning the default professional voice).
      communicationStyleBlock = service._buildCommunicationStyleBlock(communicationStyle);
      communicationStyleApplied = true;
      service.logger.info('🎨 [STYLE] CommunicationStyle injected into system prompt', {
        source: communicationStyle ? 'advisor_profile' : 'system_default',
        tone: (communicationStyle || service._getDefaultCommunicationStyle()).tone,
        formality: (communicationStyle || service._getDefaultCommunicationStyle()).formality
      });
    } else {
      service.logger.info('🎨 [STYLE] CommunicationStyle injection skipped (useStyleProfile=false)');
    }

    // 🎯 COST OPTIMIZATION: Fill template with pre-generated placeholders BEFORE sending to LLM
    // LLM only formats and adjusts tone/style (cheap operation)
    // Content is already filled with 72 pre-generated placeholders (updated 2x daily)
    service.logger.info('💰 [COST OPTIMIZATION] Filling template with pre-generated content...');
    let filledTemplateContent = params.templateContent || '';

    if (filledTemplateContent) {
      // ⚠️ CRITICAL: Check if placeholder data is production-ready
      try {
        const placeholderData = await lambdaPlaceholderService.fetchLatestPlaceholders('latest');
        const isProductionReady = placeholderData?.isProductionReady !== false;

        if (!isProductionReady) {
          const warningMsg = placeholderData?.warningMessage || 'Placeholder data is not production-ready';
          service.logger.error('🛑 [PRODUCTION SAFETY] Newsletter generation blocked:', warningMsg);

          throw new Error(
            `Cannot generate newsletter: ${warningMsg}\n\n` +
            `Placeholder data is not available from the backend. ` +
            `The system is currently using fallback/example data which cannot be used in production newsletters.`
          );
        }

        service.logger.info('✅ [PRODUCTION SAFETY] Placeholder data is production-ready');
      } catch (placeholderError) {
        // If fetchLatestPlaceholders fails, block the newsletter generation
        service.logger.error('🛑 [PRODUCTION SAFETY] Failed to verify placeholder data:', placeholderError);
        throw new Error(
          `Cannot generate newsletter: Unable to verify placeholder data is production-ready.\n\n` +
          `Error: ${placeholderError.message}`
        );
      }

      // Prepare additional client-specific data to merge with pre-generated placeholders
      let additionalData = {
        client_name: params.clientData?.name || '[Client Name]',
        newsletter_date: new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        advisor_name: params.clientData?.advisorName || '[Advisor Name]',
        company_name: params.clientData?.companyName || '[Company Name]',
        // Add any other client-specific fields
        ...params.clientData
      };

      // Phase 1B (#223): if seriesId + flag, override advisor identity
      // placeholders with values resolved from PublicationSeries + UserProfile.
      // Identity passthrough when off, so no behaviour change for existing callers.
      if (params.seriesId) {
        const seriesEnhanced = await service._applySeriesContextIfFlagged({
          systemPrompt: '',
          additionalData,
          seriesId: params.seriesId,
        });
        additionalData = seriesEnhanced.additionalData;
      }

      // Fill template with pre-generated placeholders + client data
      filledTemplateContent = lambdaPlaceholderService.fillTemplate(
        filledTemplateContent,
        additionalData
      );

      service.logger.info('💰 [COST OPTIMIZATION] ✅ Template filled with pre-generated content');
      service.logger.info(`📏 Template size: ${params.templateContent?.length} → ${filledTemplateContent.length} characters`);
    }

    // Resolve model config for routing (Issue #36: Multi-Model AI Routing)
    const modelConfig = service.getModelConfig(params.modelId);
    const resolvedModelId = modelConfig.id || DEFAULT_MODEL;

    // #313 — look up an approved EditorialBrief for this series/campaign
    // and assemble the per-section thesis block. Returns '' when the
    // flag is off, no series provided, lookup fails, no approved brief
    // exists, or every thesis is empty. Sits between voice/style and
    // the 🎯 IMPORTANT guardrail so compliance still wins on conflict.
    const editorialBriefBlock = await assembleEditorialBriefBlock(service, {
      seriesId: params.seriesId,
      campaignId: params.campaignId,
    });

    // Phase 1B (#223): build the base system prompt, then enrich with niche
    // framing + audience cues + cadence when a seriesId is supplied and
    // VITE_USE_NICHE_FRAMING is on. Identity passthrough otherwise.
    const baseSystemPrompt =
      communicationStyleBlock + stylePrompt +
      (editorialBriefBlock ? '\n\n' + editorialBriefBlock : '') +
      '\n\n🎯 IMPORTANT: The template content already includes market data and analysis. Your job is to format this content beautifully and adjust the tone/style to match the advisor\'s preferences. DO NOT regenerate or replace the existing market data - only format and refine it.';
    const enriched = await service._applySeriesContextIfFlagged({
      systemPrompt: baseSystemPrompt,
      additionalData: {},
      seriesId: params.seriesId,
    });

    // Prepare the request body with FILLED content
    const requestBody = {
      templateContent: filledTemplateContent, // ✅ Send filled template, not empty template
      clientData: params.clientData || {},
      style: params.style || advisorPrefs.style || 'professional',
      sections: params.sections || ['market_overview', 'investment_insights'],
      // Multi-model routing (Issue #36)
      modelId: params.modelId || DEFAULT_MODEL,
      provider: modelConfig.provider,
      modelApiId: resolvedModelId,
      // Add personalization (US-047) + Sprint 4 #67 voice profile (the
      // 9-dim CommunicationStyle block goes FIRST so the LLM treats it
      // as primary voice instructions, not afterthought formatting).
      systemPrompt: enriched.systemPrompt,
      writingPreferences: advisorPrefs
    };

    service.logger.info('📤 Request body prepared:', {
      hasTemplateContent: !!requestBody.templateContent,
      templateContentLength: requestBody.templateContent?.length,
      templateContentPreview: requestBody.templateContent?.substring(0, 300) + '...',
      style: requestBody.style,
      sections: requestBody.sections,
      clientName: requestBody.clientData?.name
    });

    // Fetch and inject market context if requested
    if (params.includeMarketContext !== false) {
      try {
        let marketContextInjections;

        // Use pre-fetched context if available (for batch processing)
        if (params._cachedMarketContext) {
          service.logger.info('Using cached market context for batch processing...');
          marketContextInjections = params._cachedMarketContext;
        } else {
          service.logger.info('Fetching market context for AI generation...');
          const marketContext = await service.marketContextService.getMarketContext({
            newsletterType: params.newsletterType || 'weekly',
            includeWeekly: true,
            includeQuarterly: params.newsletterType === 'quarterly',
            includeRealtime: true
          });
          marketContextInjections = marketContext.prompt_injections;

          service.logger.info(`Market context fetched: ${marketContext.metadata.total_words} words, ${marketContext.metadata.estimated_tokens} est. tokens`);
        }

        // Inject market context into the template content
        requestBody.marketContext = marketContextInjections;

        // Enhance template content with market context placeholders
        if (requestBody.templateContent && marketContextInjections) {
          requestBody.templateContent = service._injectMarketContext(
            requestBody.templateContent,
            marketContextInjections
          );
        }

        service.logger.info(`Market context injected with ${Object.keys(marketContextInjections).length} data points`);
      } catch (contextError) {
        service.logger.warn('Failed to fetch market context, proceeding without it:', contextError);
        // Continue without market context rather than failing completely
      }
    }

    // 💰 REMOVED EXPENSIVE PLACEHOLDER GENERATION (2025-10-17)
    // Previously, this section made expensive LLM calls to generate placeholder values on-demand.
    // NEW APPROACH: Pre-generated placeholders are filled into the template BEFORE sending to LLM (see lines 173-202)
    // This reduces costs dramatically:
    //   - OLD: Generate content + format = $0.02-0.05 per newsletter (expensive)
    //   - NEW: Format only = $0.001-0.003 per newsletter (cheap)
    // Pre-generated placeholders are updated 2x daily by batch-placeholder-generator Lambda
    //
    // If on-demand generation is needed in the future, use this fallback (disabled by default):
    let placeholderValues = {};
    const useExpensiveGeneration = params.forceExpensivePlaceholderGeneration || false; // Set to true only when needed

    if (useExpensiveGeneration && params.generatePlaceholders && params.template?.placeholders) {
      service.logger.warn('⚠️ Using EXPENSIVE on-demand placeholder generation. Consider using pre-generated placeholders instead.');
      try {
        service.logger.info(`Generating ${params.template.placeholders.length} placeholder values...`);

        placeholderValues = await service._generateAllPlaceholderValues(
          params.template.placeholders,
          {
            clientData: params.clientData,
            advisorPreferences: advisorPrefs,
            marketContext: requestBody.marketContext,
            templateContext: params.template,
            aiService: service
          }
        );

        // Add generated placeholders to request body
        requestBody.placeholderValues = placeholderValues;

        service.logger.info(`Generated ${Object.keys(placeholderValues).length} placeholder values successfully`);
      } catch (placeholderError) {
        service.logger.warn('Failed to generate placeholders, proceeding with template defaults:', placeholderError);
        // Continue without generated placeholders
      }
    } else {
      service.logger.info('💰 [COST OPTIMIZATION] Skipping expensive placeholder generation - using pre-filled template');
    }

    // Log final request body before API call
    service.logger.info('🚀 Making API call to:', service.apiEndpoint);
    service.logger.info('📝 Full request body:', JSON.stringify(requestBody, null, 2));
    service.logger.info('📊 Request body size:', JSON.stringify(requestBody).length, 'bytes');

    // Make request to API with provider failover (Issue #36 AC3)
    let response = await fetch(service.apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token
      },
      body: JSON.stringify(requestBody)
    });

    service.logger.info('✅ API response status:', response.status);

    // Provider failover: if primary provider returns 5xx, try alternate provider
    if (response.status >= 500) {
      const fallbackProvider = requestBody.provider === AI_PROVIDERS.OPENAI
        ? AI_PROVIDERS.BEDROCK : AI_PROVIDERS.OPENAI;
      const fallbackModels = getModelsByProvider(fallbackProvider);

      if (fallbackModels.length > 0) {
        const fallbackModel = fallbackModels[0];
        service.logger.warn(`Provider ${requestBody.provider} returned ${response.status}, failing over to ${fallbackProvider} (${fallbackModel.name})`);

        const fallbackBody = {
          ...requestBody,
          provider: fallbackProvider,
          modelId: fallbackModel.id,
          modelApiId: fallbackModel.id
        };

        response = await fetch(service.apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          },
          body: JSON.stringify(fallbackBody)
        });

        service.logger.info(`Failover response status: ${response.status}`);
      }
    }

    // Check response status
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      createNetworkError(errorData.error || `API request failed with status ${response.status}`);
    }

    // Parse response
    const data = await response.json();

    service.logger.info('📥 API response data:', {
      success: data.success,
      hasContent: !!data.content,
      contentLength: data.content?.length,
      contentPreview: data.content?.substring(0, 300) + '...',
      metadata: data.metadata
    });

    if (!data.success || !data.content) {
      service.logger.error('❌ API returned error:', data.error);
      createDataUnavailableError(data.error || 'Invalid response from AI service');
    }

    // Update usage statistics (with provider for per-provider tracking - Issue #36 AC4)
    service.updateUsageStats(data.metadata, requestBody.provider);

    const result = {
      content: data.content,
      placeholderValues, // Include generated placeholder values (US-047)
      metadata: {
        ...data.metadata || {},
        styleApplied: advisorPrefs.style,
        toneApplied: advisorPrefs.tone,
        complexityLevel: advisorPrefs.complexityLevel,
        placeholdersGenerated: Object.keys(placeholderValues).length,
        writingSampleUsed: advisorPrefs.writingSampleAnalyzed || false,
        // Sprint 4 #67 — record whether the 9-dim voice profile was applied
        communicationStyleApplied,
        communicationStyleSource: communicationStyle ? 'advisor_profile' : (useStyleProfile ? 'system_default' : 'opted_out')
      },
      success: true
    };

    service.logger.info('✨ Returning result:', {
      success: result.success,
      contentLength: result.content?.length,
      contentPreview: result.content?.substring(0, 200) + '...'
    });

    return result;
  } catch (error) {
    service.logger.error('Newsletter generation failed:', error);

    if (error.name === 'ServiceError') {
      throw error;
    }

    createNetworkError('Newsletter generation failed', error);
  }
}
